import {doppeActionTypeDefineMetadata} from '../../client-server-common/doppe-action-types/doppe-action-type';
import {wixMediaResourceReplaceExternalImage} from '@wix/devzai-common-wix';
import type {CurrencyCode, OmitStrict, PartiallyOptional} from '@wix/devzai-utils-common';
import {arrayFlatten, arrayRemoveNullValues, immerProduce, hideableValueCreateHidden, hideableValueGetVisibleValue, hideableValueIsVisibleAndNotEqualValue } from '@wix/devzai-utils-common';
import type {DoppeListActionSettings} from '../../client-server-common';
import {
    BymoPagePropsContentSettingSemanticMeaning,
    BymoPagePropsContentSettingType,
    bymoPagePropsCreateContentSettingMetadata,
    DoppeActionLayout,
    DoppeBusinessEntity,
    doppeBusinessEntityDtoIsNotFound,
    doppeListActionContentSettingsMetadataCreateForListActionWithMainDetails,
    doppeListActionGetDefaultListSettings,
    doppeListActionGetDefaultSearchListSettings,
    doppeListActionGetItemClickBehaviourDefaultListSettings,
    doppeListActionGetMainDetailsDefaultSettings,
    doppeListActionGetWithExternalDataSourceDefaultListSettings,
    doppeListActionItemCtaButtonsResetSettingsForLock,
    doppeListActionListItemCalculateItemButtons,
    doppeListActionListItemGetDefaultListItemSettingsMigration,
    doppeListActionListItemWithCtaButtonsMigration,
    doppeListActionListItemWithSectionsMigration,
    doppeListActionSettingsGetListItemCtaButtonsDefaultSettings,
    doppeListActionSettingsGetListItemSectionsDefaultSettings,
    doppeListActionTemplateCtaButtonsSettingsDefaultValuesMigration,
    doppeListActionTemplateSectionsSettingsDefaultValuesMigration,
    doppeListActionWithMainDetailsGetMainImage,
    doppeListActionWithMainDetailsReplaceMainImage
} from '../../client-server-common';
import {
    doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction
} from '../../client-server-common/types/doppe-dto-action';
import {
    doppeActionCtaButtonsResetSettingsForLock,
    DoppeLinkSpecEntityType,
    DoppeLinkSpecType
} from '../../client-server-common/types/doppe-action-cta-button';
import type {
    DoppeActionContentSectionsSettings
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import {
    doppeActionContentSectionSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import type {DoppeActionCtaButtonsSettings} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import {
    doppeActionCtaButtonsSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import {
    doppeActionGetMediaViewDefaultSettingsMigration
} from '../../client-server-common/types/doppe-action-media-view-settings';
import type {
    DoppeActionTypeHoppPricingPlan
} from '../doppe-action-type-hopp-pricing-plan/doppe-action-type-hopp-pricing-plan';
import {
    DoppeExternalMembershipPlanProvider,
    doppeExternalMembershipPlanProviderResolveBusinessEntityReference
} from '../../client-server-common/types/doppe-external-membership-plans';
import type {DoppeDtoMembershipPlan} from '../../client-server-common/types/doppe-dto-membership-plan';
import type {
    DoppeMembershipPlansExternalDataSource
} from '../../client-server-common/types/doppe-membership-plans-external-data-source';
import {
    doppeMembershipPlansExternalDataSourceResolveBusinessEntityReferences
} from '../../client-server-common/types/doppe-membership-plans-external-data-source';
import {
    doppeActionCtaButtonsResolveMainURL
} from '../doppe-actions-common/doppe-cta-button-utils/doppe-cta-button-utils';
import {
    bymoPageContextPropsResolveBusinessEntity
} from '../../doppe-sdk/bymo-page/bymo-page-context-props';
import type {WixMediaResource} from '@wix/devzai-common-wix';


export namespace DoppeActionTypeHoppPricingPlans {

    export interface PricingPlanSettings extends
        DoppeListActionSettings.ListItemBaseSettings,
        DoppeListActionSettings.ListItemWithCtaButtons,
        DoppeListActionSettings.ListItemWithSections,
        OmitStrict<DoppeActionTypeHoppPricingPlan.ActionSettings, 'ctaButtons' | 'sections'> {
    }

    export interface ActionSettings extends
        DoppeListActionSettings<PricingPlanSettings>,
        DoppeActionContentSectionsSettings,
        DoppeActionCtaButtonsSettings,
        DoppeListActionSettings.WithSearchSettings,
        DoppeListActionSettings.WithMainDetails,
        DoppeListActionSettings.WithItemClickBehaviour,
        DoppeListActionSettings.WithExternalDataSourceSupport<DoppeMembershipPlansExternalDataSource> {
        previewShowTitle: boolean;
        previewShowDescription: boolean;
        previewShowPrice: boolean;
        previewShowCoupon: boolean;
    }
}

export const doppeActionTypeHoppPricingPlans = doppeActionTypeDefineMetadata<DoppeActionTypeHoppPricingPlans.ActionSettings>({
    id: 'io.bymo.action/hopp-pricing-plans',
    prepareForViewer: (actionSettings, options) => {
        doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction(actionSettings, actionSettings => {
            doppeListActionItemCtaButtonsResetSettingsForLock(actionSettings);
            doppeActionCtaButtonsResetSettingsForLock(actionSettings);
        }, options);
    },
    resolveActionSettings: action => {

        const {
            sections,
            listDataItems = [],
            ctaButtons,
            listItemTemplateCtaButtonsSettings,
            listItemTemplateSectionsSettings,
            listSearchSettings,
            ...restSettings
        } = action.settings;

        return {
            itemShowPrice: true,
            previewShowCoupon: true,
            previewShowPrice: true,
            previewShowDescription: true,
            previewShowTitle: true,
            ...doppeListActionGetWithExternalDataSourceDefaultListSettings(),
            ...doppeListActionGetItemClickBehaviourDefaultListSettings(),
            ...doppeListActionGetMainDetailsDefaultSettings(),
            ...doppeListActionGetDefaultSearchListSettings(action.settings),
            ...doppeListActionGetDefaultListSettings({
                layout: DoppeActionLayout.Column,
                listDataItems: listDataItems.map(pricingPlan => {

                    const {
                        ctaButtonsOverridesMap,
                        customCtaButtons,
                        mainMediaViewSettings,
                        mainMediaViewLayout,
                        id,
                        deleted,
                        displayInSearch,
                        enabled,
                        ...restPricingPlan
                    } = pricingPlan;

                    return {
                        ...doppeActionGetMediaViewDefaultSettingsMigration(pricingPlan),
                        ...doppeListActionListItemWithCtaButtonsMigration(pricingPlan, false),
                        ...doppeListActionListItemWithSectionsMigration(pricingPlan),
                        ...doppeListActionListItemGetDefaultListItemSettingsMigration(pricingPlan.id, pricingPlan),
                        ...restPricingPlan as PartiallyOptional<DoppeActionTypeHoppPricingPlans.PricingPlanSettings, 'mainMediaViewSettings' | 'mainMediaViewLayout'  | 'ctaButtonsOverridesMap' |'customCtaButtons' | 'sectionsOverridesMap' | 'customSections'>
                    }
                }),
            }),
            ...doppeActionContentSectionSettingsDefaultValuesMigration(action.settings),
            ...doppeActionCtaButtonsSettingsDefaultValuesMigration(action.settings, false),
            ...doppeListActionTemplateCtaButtonsSettingsDefaultValuesMigration(action.settings, false),
            ...doppeListActionTemplateSectionsSettingsDefaultValuesMigration(action.settings),
            ...restSettings
        }
    },
    actionSettingsMetadata: {
        contentSettingsMetadata: {
            ...doppeListActionContentSettingsMetadataCreateForListActionWithMainDetails()
        },
        supportsActionCtaButtons: {
            customCtaButtonTypes: {}
        },
        supportsContentSections: {
            customContentSectionsTypes: {}
        },
        hideableContentSettingsNames: {
                mainImage: true,
                mainTitle: true
            }
    },
    actionListItemSettingsMetadata: {
        contentSettingsMetadata: {
            pricingPlanPageLink: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Url, BymoPagePropsContentSettingSemanticMeaning.EntityPageUrl),
            pricingPlanTitle: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityTitle),
            description: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.RichText, BymoPagePropsContentSettingSemanticMeaning.EntityDescription),
            pricingPlanImages: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.MultipleMedia, BymoPagePropsContentSettingSemanticMeaning.EntityMedia),
            pricingPlanBenefits: 'Ignored',
            couponCode: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityCouponCode),
            ribbon: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityRibbon)
        },
        supportsActionCtaButtons: {
            customCtaButtonTypes: {}
        },
        supportsContentSections: {
            customContentSectionsTypes: {}
        },
        hideableContentSettingsNames: {
                couponCode: true,
                description: true,
                priceData: true,
                pricingPlanTitle: true,
                ribbon: true
            }
    },
    isWidgetOpener: true,
    canRenderInPage: true,
    skipReportingOnGoalAchievedOnActionButtonClick: true,

    isListItemLinkOpener: true,
    supportsListLayout: true,
    resolveListItems: (action, viewerLang, bymoPageProps, viewerPagesRoutesManager, bymoPageContextProps) => {
        const actionSettings = action.settings;
        return actionSettings.listDataItems.map(item => {
            // Try to resolve the pricing plan entity from business entities data
            const pricingPlanEntity = item.pricingPlanProviderId ? bymoPageContextPropsResolveBusinessEntity(
                bymoPageContextProps,
                {businessEntity: DoppeBusinessEntity.HoppPricingPlan, id: item.pricingPlanProviderId}
            ) : undefined;

            // Check if we have valid entity data
            const hasValidEntity = pricingPlanEntity && !doppeBusinessEntityDtoIsNotFound(pricingPlanEntity);

            // Resolve values from entity or fall back to stored settings
            const title = hasValidEntity
                ? (pricingPlanEntity.name ?? hideableValueGetVisibleValue(item.pricingPlanTitle, ''))
                : hideableValueGetVisibleValue(item.pricingPlanTitle, '');

            const description = hasValidEntity
                ? (pricingPlanEntity.description ?? hideableValueGetVisibleValue(item.description, ''))
                : hideableValueGetVisibleValue(item.description, '');

            // Resolve image from entity or fall back to stored settings
            // PricingPlan has a single media image (WixImageInfo which is compatible with WixMediaResource)
            const entityImage = hasValidEntity && pricingPlanEntity.media
                ? (pricingPlanEntity.media as WixMediaResource)
                : null;
            const storedImage = item.pricingPlanImages?.[0] ?? null;
            const image = entityImage ?? storedImage;

            // Calculate URL from link spec
            const url = doppeActionCtaButtonsResolveMainURL(
                doppeListActionListItemCalculateItemButtons(actionSettings.listItemTemplateCtaButtonsSettings, item),
                {
                    sourceLinkSpec: item.pricingPlanProviderId ? {
                        type: DoppeLinkSpecType.Entity,
                        entityType: DoppeLinkSpecEntityType.PricingPlan,
                        entityId: item.pricingPlanProviderId
                    } : null,
                    viewerLang: viewerLang,
                    bymoPageProps: bymoPageProps,
                    viewerPagesRoutesManager: viewerPagesRoutesManager,
                    bymoPageContextProps: bymoPageContextProps
                }
            ) ?? null;

            return {
                title: title,
                action: action,
                couponCode: item.couponCode ?? hideableValueCreateHidden(''),
                description: hideableValueCreateHidden(description),
                image: image,
                itemId: item.id,
                ribbon: item.ribbon ?? hideableValueCreateHidden(''),
                url: url,
                enabled: item.enabled,
                deleted: item.deleted,
                displayInSearch: item.displayInSearch,
            }
        });
    },
    resolveListSearchSettings: actionSettings => {
        return actionSettings.listSearchSettings;
    },
    resolveUsedMedia: actionSettings => {
        const listItemsImages = arrayFlatten(actionSettings.listDataItems.map(item => item.pricingPlanImages ?? []));
        const pricingPlan = actionSettings.listDataItems[0];
        const mediaItems = pricingPlan ? arrayRemoveNullValues(pricingPlan.pricingPlanImages ?? []) : [];
        const mainDetailsImage = doppeListActionWithMainDetailsGetMainImage(actionSettings, mediaItems[0]);

        return arrayRemoveNullValues([...listItemsImages, mainDetailsImage]);
    },
    resolveMainMedia: actionSettings => {
        const pricingPlan = actionSettings.listDataItems[0];
        const mediaItems = pricingPlan ? arrayRemoveNullValues(pricingPlan.pricingPlanImages ?? []) : [];
        return doppeListActionWithMainDetailsGetMainImage(actionSettings, mediaItems[0]);
    },
    resolveUsedBusinessEntities: actionSettings => {
        const externalDataSource = actionSettings.externalDataSource;
        if (externalDataSource) {
            return [doppeMembershipPlansExternalDataSourceResolveBusinessEntityReferences(externalDataSource)];
        } else {
            return arrayRemoveNullValues(actionSettings.listDataItems.map(item => {
                return item.pricingPlanProvider !== null && item.pricingPlanProviderId !== null ?
                    doppeExternalMembershipPlanProviderResolveBusinessEntityReference(
                        item.pricingPlanProvider,
                        item.pricingPlanProviderId
                    ) :
                    null
            }));
        }
    },
    resolveMainLink: (actionSettings, viewerLang, bymoPageProps, viewerPagesRoutesManager, bymoPageContextProps) => {
        return doppeActionCtaButtonsResolveMainURL(actionSettings, {
            sourceLinkSpec: null,
            viewerLang: viewerLang,
            bymoPageProps: bymoPageProps,
            viewerPagesRoutesManager: viewerPagesRoutesManager,
            bymoPageContextProps: bymoPageContextProps
        }) ?? null;
    },
    resolveCouponData: actionSettings => {
        const firstItemWithCoupon = actionSettings.listDataItems.find(item => {
            return hideableValueIsVisibleAndNotEqualValue(item.couponCode, '');
        });

        if (firstItemWithCoupon) {
            return {
                showCouponCode: true,
                couponCode: hideableValueGetVisibleValue(firstItemWithCoupon.couponCode, ''),
                couponDescription: '',
                couponTitle: hideableValueGetVisibleValue(firstItemWithCoupon.pricingPlanTitle, ''),
            }
        }

        return {
            showCouponCode: false,
            couponCode: '',
            couponDescription: '',
            couponTitle: '',
        }
    },
    replaceExternalImages: (actionSettings, externalImagesUrlToImageResourceMap) => {
        return immerProduce(actionSettings, actionSettings => {
            for (const pricingPlan of actionSettings.listDataItems) {
                if (pricingPlan.pricingPlanImages) {
                    pricingPlan.pricingPlanImages = pricingPlan.pricingPlanImages.map(
                        image => wixMediaResourceReplaceExternalImage(image, externalImagesUrlToImageResourceMap))
                }
            }

            doppeListActionWithMainDetailsReplaceMainImage(actionSettings, externalImagesUrlToImageResourceMap);

        })
    },
});

export function convertDoppeDtoMembershipPlanToHoppPricingPlansSettings(pricingPlan: DoppeDtoMembershipPlan, pricingPlanProvider: DoppeExternalMembershipPlanProvider = DoppeExternalMembershipPlanProvider.Hopp): DoppeActionTypeHoppPricingPlans.PricingPlanSettings {
    return {
        ...doppeListActionListItemGetDefaultListItemSettingsMigration(pricingPlan.id),
        pricingPlanPageLink: pricingPlan.membershipPageUrl ?? '',
        pricingPlanTitle: pricingPlan.name ?? '',
        description: pricingPlan.description ?? '',
        pricingPlanImages: pricingPlan.media ? [pricingPlan.media] : null,
        priceData: pricingPlan.pricing ? {
            actualPrice: parseFloat(pricingPlan.pricing.price?.value ?? '0') || 0,
            currencyCode: (pricingPlan.pricing.price?.currency ?? 'USD') as CurrencyCode
        } : {
            actualPrice: 0,
            discountPrice: undefined,
            currencyCode: 'USD' as CurrencyCode
        },
        pricingPlanBenefits: pricingPlan.perks ?? [],
        couponCode: hideableValueCreateHidden(''),
        ribbon: hideableValueCreateHidden(''),
        pricingPlanProvider: pricingPlanProvider,
        pricingPlanProviderId: pricingPlan.id,
        ...doppeActionGetMediaViewDefaultSettingsMigration({}),
        ...doppeListActionSettingsGetListItemCtaButtonsDefaultSettings(),
        ...doppeListActionSettingsGetListItemSectionsDefaultSettings(),
    } satisfies DoppeActionTypeHoppPricingPlans.PricingPlanSettings
} 